import { I18n } from "@lingui/core";
import _ from "lodash";
import {
  AccountType,
  CreditorBankAccountCreateRequestBody,
} from "@gocardless/api/dashboard/types";

import ATFields from "./country_fields/fields.config.AT";
import AUFields from "./country_fields/fields.config.AU";
import AXFields from "./country_fields/fields.config.AX";
import BEFields from "./country_fields/fields.config.BE";
import BLFields from "./country_fields/fields.config.BL";
import CAFields from "./country_fields/fields.config.CA";
import CYFields from "./country_fields/fields.config.CY";
import DEFields from "./country_fields/fields.config.DE";
import DKFields from "./country_fields/fields.config.DK";
import EEFields from "./country_fields/fields.config.EE";
import ESFields from "./country_fields/fields.config.ES";
import FIFields from "./country_fields/fields.config.FI";
import FRFields from "./country_fields/fields.config.FR";
import GBFields from "./country_fields/fields.config.GB";
import GFFields from "./country_fields/fields.config.GF";
import GPFields from "./country_fields/fields.config.GP";
import GRFields from "./country_fields/fields.config.GR";
import IEFields from "./country_fields/fields.config.IE";
import ITFields from "./country_fields/fields.config.IT";
import LTFields from "./country_fields/fields.config.LT";
import LUFields from "./country_fields/fields.config.LU";
import LVFields from "./country_fields/fields.config.LV";
import MCFields from "./country_fields/fields.config.MC";
import MFFields from "./country_fields/fields.config.MF";
import MQFields from "./country_fields/fields.config.MQ";
import MTFields from "./country_fields/fields.config.MT";
import NLFields from "./country_fields/fields.config.NL";
import NZFields from "./country_fields/fields.config.NZ";
import PMFields from "./country_fields/fields.config.PM";
import PTFields from "./country_fields/fields.config.PT";
import REFields from "./country_fields/fields.config.RE";
import SEFields from "./country_fields/fields.config.SE";
import SIFields from "./country_fields/fields.config.SI";
import SKFields from "./country_fields/fields.config.SK";
import SMFields from "./country_fields/fields.config.SM";
import USFields from "./country_fields/fields.config.US";
import YTFields from "./country_fields/fields.config.YT";
import BGFields from "./country_fields/fields.config.BG";
import CHFields from "./country_fields/fields.config.CH";
import CZFields from "./country_fields/fields.config.CZ";
import HRFields from "./country_fields/fields.config.HR";
import HUFields from "./country_fields/fields.config.HU";
import NOFields from "./country_fields/fields.config.NO";
import PLFields from "./country_fields/fields.config.PL";
import ROFields from "./country_fields/fields.config.RO";
import ZAFields from "./country_fields/fields.config.ZA";

import { CustomField } from "src/components/ui/CustomMetadataField/CustomMetadataField";
import { CountryCodes, CountryCodeSupported } from "src/common/country";

export interface InputFormatterOptions {
  delimiter?: string | string[];
  parts?: number[];
  maxLength?: number;
  onlyNumbers?: boolean;
}
// FieldNames that are populated by the bank detail form. Some locales provide a
// different set of fields to others.
export enum FieldName {
  accountName = "account_holder_name",
  accountNumber = "account_number",
  accountNumberSuffix = "account_suffix",
  accountType = "account_type",
  bankCode = "bank_code",
  branchCode = "branch_code",
  countryCode = "country_code",
  currency = "currency",
  iban = "iban",
}

// For each field, define the properties we should use to display it. These are
// translated via an i18n configuration.
export interface FieldConfig {
  name: FieldName;
  label: string;
  placeholder?: string;
  instructions?: string;
  options?: object;
  pattern?: RegExp;
  formatterOptions?: InputFormatterOptions;
}

// For each country, we have a set of field configs for either iban or the
// preferred local details, if they are available.
export interface CountryFieldConfig {
  iban?: FieldConfig;
  local?: FieldConfig[];
  primaryBankDetailsFields?: FieldName[];
  secondaryBankDetailsField?: FieldName[];
}

type Generators = {
  [key in CountryCodeSupported]: (i18n: I18n) => CountryFieldConfig;
};

// This config specifies all the bank account form field details. It allows us
// to generate the appropriate fields for each country code.
//
// Unlike customer details, for which we expose field config via the
// payments-service API, we're unsure if we should ever make this API servable.
// The bank account collection step is something we want to encourage people to
// leave to us, as we may be able to skip it. Until there's demand for the field
// config to be shared, it may be best to keep it inside this app.
//
// TODO: We only have english translations for now, but we'll have all of these
// files in all supported languages. We will then load them at runtime.
export const FieldConfigGenerators: Generators = {
  AT: ATFields,
  AU: AUFields,
  AX: AXFields,
  BE: BEFields,
  BG: BGFields,
  BL: BLFields,
  CA: CAFields,
  CH: CHFields,
  CY: CYFields,
  CZ: CZFields,
  DE: DEFields,
  DK: DKFields,
  EE: EEFields,
  ES: ESFields,
  FI: FIFields,
  FR: FRFields,
  GB: GBFields,
  GF: GFFields,
  GP: GPFields,
  GR: GRFields,
  HR: HRFields,
  HU: HUFields,
  IE: IEFields,
  IT: ITFields,
  LT: LTFields,
  LU: LUFields,
  LV: LVFields,
  MC: MCFields,
  MF: MFFields,
  MQ: MQFields,
  MT: MTFields,
  NL: NLFields,
  NO: NOFields,
  NZ: NZFields,
  PL: PLFields,
  PM: PMFields,
  PT: PTFields,
  RE: REFields,
  RO: ROFields,
  SE: SEFields,
  SI: SIFields,
  SK: SKFields,
  SM: SMFields,
  US: USFields,
  YT: YTFields,
  ZA: ZAFields,
};

export default FieldConfigGenerators;

export const forCountry = (country: CountryCodeSupported, i18n: I18n) =>
  FieldConfigGenerators[country](i18n);

export interface Country {
  code?: CountryCodes;
  name: string;
}

export interface BankAccountForm {
  currency?: string;
  country_code?: string;
  iban?: string;
  bank_code?: string;
  branch_code?: string;
  account_number?: string;
  account_suffix?: string;
  account_type?: AccountType | null | undefined;
  metadata?: CustomField[];
  account_holder_name: string;
}

type BankDetails = CreditorBankAccountCreateRequestBody | BankAccountForm;
export const cleanupBankDetails = (bankDetails: BankDetails): BankDetails => {
  // BECSNZ displays both account_number and account_suffix but our API
  // expects them to come together in account_number.
  if (_.get(bankDetails, FieldName.accountNumberSuffix)) {
    const mergedAccountNumber = `${_.get(
      bankDetails,
      FieldName.accountNumber
    )}-${_.get(bankDetails, FieldName.accountNumberSuffix)}`;
    return {
      ..._.omit(bankDetails, [FieldName.accountNumberSuffix]),
      account_number: mergedAccountNumber,
    } as BankDetails;
  }
  return bankDetails;
};
